import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronRight, MessageSquareWarning, X, Zap, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { devError } from '../lib/constants';
import NudgeDialog from './NudgeDialog';

const MOOD_STYLES = {
  clear: { bg: 'bg-emerald-600', text: 'text-white', border: 'border-emerald-700', accent: 'bg-emerald-700', icon: CheckCircle2 },
  attention: { bg: 'bg-[#FFD700]', text: 'text-zinc-900', border: 'border-zinc-900', accent: 'bg-zinc-900 text-white', icon: Sparkles },
  urgent: { bg: 'bg-red-600', text: 'text-white', border: 'border-red-800', accent: 'bg-red-800', icon: AlertTriangle },
};

const KIND_ICON = {
  overdue: AlertTriangle,
  lagging: MessageSquareWarning,
  fresh: Sparkles,
};

export default function DailyDigestBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [digest, setDigest] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [nudgeOpen, setNudgeOpen] = useState(false);

  useEffect(() => {
    if (user?.role !== 'boss' && user?.role !== 'manager') return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/digest/daily');
        if (!cancelled) setDigest(data);
      } catch (err) {
        devError('digest failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!digest || dismissed) return null;
  if (user?.role !== 'boss' && user?.role !== 'manager') return null;

  const style = MOOD_STYLES[digest.mood] || MOOD_STYLES.attention;
  const HeadIcon = style.icon;

  const openItem = (it) => {
    if (it.task_id) navigate(`/tasks/${it.task_id}`);
    else if (it.kind === 'lagging') navigate('/snapshot');
    else if (it.kind === 'fresh') navigate('/');
  };

  // Build recipient list for bulk nudge (from lagging items + lagging_user_ids)
  const laggingFromItems = digest.items.filter(i => i.kind === 'lagging');
  const laggingRecipients = (digest.lagging_user_ids || laggingFromItems.map(i => i.user_id))
    .map((uid) => {
      const item = laggingFromItems.find(i => i.user_id === uid);
      return { id: uid, name: item?.user_name || 'Team member' };
    });
  const showBulkNudge = laggingRecipients.length >= 2;

  return (
    <div data-testid="daily-digest" className={`${style.bg} ${style.text} border ${style.border} shadow-brutal-sm`}>
      <div className="flex items-start gap-3 px-5 py-4">
        <div className={`${style.accent} ${digest.mood === 'attention' ? '' : 'bg-white/10'} p-2 flex items-center justify-center shrink-0`}>
          <HeadIcon size={20} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">DAILY DIGEST · {new Date(digest.generated_at).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}</div>
          <div className="font-display text-xl sm:text-2xl font-black tracking-tighter leading-snug">{digest.headline}</div>
          {digest.stats && (digest.stats.new_updates_yesterday > 0 || digest.stats.resolved_yesterday > 0) && (
            <div className="text-xs mt-1 opacity-90 flex flex-wrap gap-x-3 gap-y-1" data-testid="digest-stats">
              {digest.stats.new_updates_yesterday > 0 && <span>· {digest.stats.new_updates_yesterday} new update{digest.stats.new_updates_yesterday !== 1 ? 's' : ''} yesterday</span>}
              {digest.stats.resolved_yesterday > 0 && <span>· {digest.stats.resolved_yesterday} resolved yesterday</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {showBulkNudge && (
            <button
              onClick={() => setNudgeOpen(true)}
              data-testid="digest-bulk-nudge"
              className={`${style.accent} ${digest.mood === 'attention' ? '' : 'bg-white/15 hover:bg-white/25'} flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em]`}
              title={`Nudge all ${laggingRecipients.length} stale people at once`}
            >
              <Zap size={12} /> Nudge all stale ({laggingRecipients.length})
            </button>
          )}
          {digest.items.length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              data-testid="digest-toggle"
              className={`${style.accent} ${digest.mood === 'attention' ? '' : 'bg-white/15 hover:bg-white/25'} px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em]`}
            >
              {expanded ? 'Hide' : 'Show'}
            </button>
          )}
          <button onClick={() => setDismissed(true)} data-testid="digest-dismiss" className="p-1 opacity-70 hover:opacity-100" title="Dismiss until next session"><X size={16} /></button>
        </div>
      </div>

      {expanded && digest.items.length > 0 && (
        <ul className={`border-t ${style.border} divide-y ${style.border}`}>
          {digest.items.map((it, idx) => {
            const Icon = KIND_ICON[it.kind] || Zap;
            return (
              <li key={`${it.kind}-${it.task_id || it.user_id || idx}`}>
                <button
                  onClick={() => openItem(it)}
                  data-testid={`digest-item-${it.kind}-${idx}`}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-black/10 transition-colors`}
                >
                  <Icon size={14} strokeWidth={2.2} className="opacity-80 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{it.title}</div>
                    <div className="text-xs opacity-80 truncate">{it.detail}</div>
                  </div>
                  <ChevronRight size={14} className="opacity-60 shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <NudgeDialog
        open={nudgeOpen}
        recipients={laggingRecipients}
        onClose={() => setNudgeOpen(false)}
      />
    </div>
  );
}
